/* eslint-disable import/first */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import '../../DetailsCard.css';
import '../../detailscardSliders.css';
import TopSightsCarouselV2 from './TopSightsCarouselV2';
import HistoricalPeriodsV2 from './HistoricalPeriodsV2';
import LocalFoodV2 from './LocalFoodV2';
import WikipediaTabV2 from './WikipediaTabV2';
import WikiImagesSliderV2 from './WikiImagesSliderV2';
import { useDisplayLocation } from '../../signals/displayLocation';
import { useTopicStore } from './store';
import type { LocationTopic, WikiTopic, Topic, UnderstandingPoint } from './types/detailsCard.types';
import useCardDrag from './hooks/useCardDrag';
import useHorizontalPointerDrag from './hooks/useHorizontalPointerDrag';
import { playHistorySuctionAnimation } from '../../utils/historySuctionAnimation';
import ThinkingIndicator from '../../components/ThinkingIndicator';
import SigninPopup from '../../SigninPopup';
import { loginMachine, useLoginStore } from '../../auth/loginMachine';

// JS components – cast to any to avoid TS prop errors during migration
const AudioStatusWidget: any = require('../../AudioStatusWidget').default;
const QuotaModal: any = require('../../QuotaModal').default;
/* eslint-enable import/first */

const LOGIN_PROMPT_MIN_HEIGHT_VH = 45;
const AUDIO_GUIDES_INITIAL_HEIGHT_VH = 45;
const AUDIO_GUIDES_MIN_HEIGHT_VH = 62;

// ---------------------------------------------------------------------------
// Props – mirrors App.js <DetailsCard ... /> prop-set (lines 2592-2634)
// ---------------------------------------------------------------------------
export interface DetailsCardV2Props {
  article?: any;
  cityName?: string;
  prefetchedLocationPopulation?: number | null;
  viewportWikiLanguage?: string;
  userLanguageLabel?: string;
  userPreferredWikiLangCode?: string;
  onClose?: () => void;
  startNarration?: (...args: any[]) => void;
  onArticleSelected?: (article: any) => void;
  onTopSightShowOnMap?: (sight: any) => void;
  onTopSightFocus?: (sight: any | null) => void;
  gpsAvailable?: boolean | null;
  isGuestAccount?: boolean;
  showPwaInstallButton?: boolean;
  onPwaInstallClick?: () => void;
  onControlsRender?: (fn: any) => void;
  onSignInClick?: () => void;
  onHeightChange?: (height: number) => void;
  onGoToMyLocation?: (() => void) | null;
  onToggleMapType?: (() => void) | null;
  onOpenUserHistory?: (() => void) | null;
  isMapCenteredOnUser?: boolean;
  audioEnabled?: boolean;
  audioStatus?: string;
  audioTitle?: string;
  audioMode?: string;
  onAudioPauseToggle?: () => void;
  onAudioTogglePlayPause?: () => void;
  onAudioStop?: () => void;
  onAudioSeek?: (time: number) => void;
  audioCurrentTime?: number;
  audioDuration?: number;
}

// ---------------------------------------------------------------------------
// Imperative handle exposed to parent via ref
// ---------------------------------------------------------------------------
export interface DetailsCardV2Handle {
  handlePeriodsClick: () => void;
  handleFoodClick: () => void;
  switchToLocation: (locData: any) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatPopulationCompact = (value: number | null | undefined): string | null => {
  if (value == null || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(value!);
};

/** Build a stable topic-id from locationData. */
function locationToTopicId(loc: any): string {
  const name = getLocationName(loc);
  if (name) return `location:name:${name.trim().toLowerCase().replace(/\s+/g, ' ')}`;
  const lat = Number(loc?.lat);
  const lon = Number(loc?.lon);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return `location:coords:${lat.toFixed(3)},${lon.toFixed(3)}`;
  }
  return 'location:unknown';
}

function getLocationName(loc: any): string {
  if (!loc) return '';
  if (loc.topic_place_name) return loc.topic_place_name;
  const addr = loc.address;
  if (addr) {
    const fromAddr = addr.city || addr.town || addr.village || addr.hamlet || addr.county || addr.state || addr.country;
    if (fromAddr) return fromAddr;
  }
  return loc.display_name || addr?.display_name || loc.name || '';
}

function trimOverviewText(input: string, maxChars = 140): string {
  const text = String(input || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';

  const sentenceParts = text.match(/[^.!?]+[.!?]+/g) || [];
  const oneSentence = sentenceParts.slice(0, 1).join(' ').trim();
  const candidate = oneSentence || text;

  if (candidate.length <= maxChars) return candidate;
  return `${candidate.slice(0, maxChars).trimEnd()}…`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const DetailsCardV2 = forwardRef<DetailsCardV2Handle, DetailsCardV2Props>((props, ref) => {
  const {
    article,
    viewportWikiLanguage = 'en',
    userLanguageLabel = 'English',
    userPreferredWikiLangCode = 'en',
    onArticleSelected = () => {},
    onTopSightShowOnMap = () => {},
    onTopSightFocus = () => {},
    isGuestAccount = true,
    showPwaInstallButton = false,
    onPwaInstallClick,
    onSignInClick = () => {},
    onHeightChange,
    onGoToMyLocation,
    onToggleMapType,
    onOpenUserHistory,
    isMapCenteredOnUser = false,
    startNarration = () => {},
    audioEnabled = false,
    audioStatus = 'idle',
    audioTitle = '',
    audioMode = 'overview',
    onAudioPauseToggle,
    onAudioTogglePlayPause,
    onAudioStop,
    onAudioSeek,
    audioCurrentTime = 0,
    audioDuration = 0,
  } = props;

  // ---- Zustand store --------------------------------------------------
  const {
    topics,
    activeTopicId,
    suggestedTopicId,
    setTopic,
    activateSuggestedTopic,
    fetchLocalFoods,
    fetchTopSights,
    fetchWikipediaEmbed,
    fetchUnderstandingConcepts,
    fetchMoreUnderstandingConcepts,
  } = useTopicStore();
  const activeTopic: Topic | null = activeTopicId ? topics[activeTopicId] ?? null : null;
  const suggestedTopic = suggestedTopicId ? topics[suggestedTopicId] ?? null : null;
  const isWiki = activeTopic?.type === 'wiki';
  const isLocation = activeTopic?.type === 'location';
  const isPrompt = isWiki && (activeTopic as WikiTopic).pageId === 'prompt';

  // Convenience casts
  const wikiTopic = isWiki ? (activeTopic as WikiTopic) : null;
  const locationTopic = isLocation ? (activeTopic as LocationTopic) : null;

  // ---- Location signal (current GPS / map-center) ---------------------
  const locationData = useDisplayLocation();

  // ---- Refs -----------------------------------------------------------
  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const audioWidgetAnchorRef = useRef<HTMLDivElement>(null);

  // ---- Card drag / resize gesture -------------------------------------
  const { heightPx, onPointerDown: onCardPointerDown, isDragging, ensureMinHeightVh } = useCardDrag({
    contentRef,
    initialVh: AUDIO_GUIDES_INITIAL_HEIGHT_VH,
    onHeightChange,
  });

  // ---- UI state -------------------------------------------------------
  const [activeTab, setActiveTab] = useState<string | null>('audio-tab');
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [lastLocationButtonPressPosition, setLastLocationButtonPressPosition] = useState<{ lat: number; lon: number } | null>(null);
  const initialLocationBaselineRef = useRef<{ lat: number; lon: number } | null>(null);
  const understandSliderRef = useHorizontalPointerDrag<HTMLDivElement>();
  const mainTabsSliderRef = useHorizontalPointerDrag<HTMLDivElement>({ ignoreInteractiveTargets: false });

  // Derived flags
  const hasLocationWikiAvailable = isLocation && !!locationTopic?.wikidataTag;
  const understandPlaceName = String(activeTopic?.name || getLocationName(locationData) || '').trim();
  const understandButtonLabel = 'Understand';
  const understandState = activeTopic?.understandingConcepts ?? null;
  const understandLoading = !!understandState?.isLoading;
  const understandStreaming = !!understandState?.isStreaming;
  const understandError = understandState?.error ?? null;
  const understandPoints: UnderstandingPoint[] = understandState?.items ?? [];
  const fallbackLocationName = getLocationName(locationData) || 'Location';
  const locationButtonTopicName = suggestedTopic?.name || fallbackLocationName;
  const shouldShowLocationButton = !!suggestedTopic || !!locationData;
  const loginUi = useLoginStore((state) => state.ui);
  const loginAccount = useLoginStore((state) => state.account);
  const showCardLoginPrompt = loginUi.kind === 'DetailsCardLoginPrompt' || loginUi.kind === 'LoginBusy';
  const isLoginAnonymous = loginAccount.kind === 'AnonAccount' || loginAccount.kind === 'AnonLoginPending';

  useEffect(() => {
    if (audioStatus === 'idle') return;
    if (isPrompt) return;
    if (activeTab !== 'audio-tab') return;
    ensureMinHeightVh(AUDIO_GUIDES_MIN_HEIGHT_VH);
  }, [audioStatus, isPrompt, activeTab, ensureMinHeightVh]);

  useEffect(() => {
    if (!showCardLoginPrompt) return;
    ensureMinHeightVh(LOGIN_PROMPT_MIN_HEIGHT_VH);
  }, [showCardLoginPrompt, ensureMinHeightVh]);

  // ---- Population / article-size derived gates -------------------------
  const populationValue = locationTopic?.population ?? null;
  const ANECDOTE_MIN_LOCATION_POPULATION = 700_000;
  const ANECDOTE_MIN_WIKI_ARTICLE_SIZE_BYTES = 45_000;
  const wikiArticleSizeBytes = wikiTopic?.articleSizeBytes ?? null;
  const showAnecdoteTab = isLocation
    ? (Number(populationValue) > ANECDOTE_MIN_LOCATION_POPULATION)
    : (isWiki && Number(wikiArticleSizeBytes) >= ANECDOTE_MIN_WIKI_ARTICLE_SIZE_BYTES);

  useEffect(() => {
    const onAudioCompleted = (evt: Event) => {
      const customEvent = evt as CustomEvent<{ mode?: string }>;
      const mode = customEvent?.detail?.mode;
      const label = mode === 'anecdote' ? 'Anecdote' : 'History';
      playHistorySuctionAnimation({
        sourceEl: audioWidgetAnchorRef.current,
        targetEl: historyButtonRef.current,
        label,
      });
    };

    window.addEventListener('audioCompleted', onAudioCompleted as EventListener);
    return () => window.removeEventListener('audioCompleted', onAudioCompleted as EventListener);
  }, []);

  const calculateDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const hasMovedSignificantly = (): boolean => {
    if (!locationData) return false;

    const currentLat = Number(locationData?.lat);
    const currentLon = Number(locationData?.lon ?? locationData?.lng);
    if (!Number.isFinite(currentLat) || !Number.isFinite(currentLon)) return false;

    // One-time baseline without useEffect: first valid location becomes the
    // initial reference (legacy behavior), until user presses location button.
    if (!initialLocationBaselineRef.current) {
      initialLocationBaselineRef.current = { lat: currentLat, lon: currentLon };
    }

    const reference = lastLocationButtonPressPosition ?? initialLocationBaselineRef.current;
    if (!reference) return false;

    const distance = calculateDistanceKm(
      reference.lat,
      reference.lon,
      currentLat,
      currentLon,
    );

    return distance > 5;
  };

  // ---- Tab action handlers (stubbed) ----------------------------------
  const handlePeriodsClick = () => {
    ensureMinHeightVh(45);
  };

  const handleFoodClick = () => {
    ensureMinHeightVh(45);
    if (activeTopicId) {
      void fetchLocalFoods(activeTopicId, { userLanguageLabel, viewportWikiLanguage });
    }
  };

  const handleWikipediaTabClick = () => {
    if (activeTopicId) {
      void fetchWikipediaEmbed(activeTopicId, { viewportWikiLanguage, userPreferredWikiLangCode });
    }
  };

  const handleLocationButtonClick = () => {
    const lat = Number(locationData?.lat);
    const lon = Number(locationData?.lon ?? locationData?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lon)) {
      setLastLocationButtonPressPosition({ lat, lon });
    }

    if (suggestedTopic) {
      console.log('[DetailsCardV2] Location button pressed - activating suggested topic.');
      activateSuggestedTopic({
        userLanguageLabel,
        viewportWikiLanguage,
        userPreferredWikiLangCode,
      });
      return;
    }

    if (!locationData) return;

    console.log('[DetailsCardV2] Location button pressed - activating current location topic.');
    const id = locationToTopicId(locationData);
    const locTopic: LocationTopic = {
      id,
      type: 'location',
      name: getLocationName(locationData),
      lat: locationData.lat,
      lng: locationData.lon ?? locationData.lng,
      wikidataTag: locationData.topic_wikidata,
      population: locationData.topic_population,
    };

    setTopic(locTopic);
    onArticleSelected(null);
    void fetchTopSights(id, {
      userLanguageLabel,
      viewportWikiLanguage,
      userPreferredWikiLangCode,
    });
  };

  const handleUnderstandPointRead = (point: UnderstandingPoint) => {
    const place = understandPlaceName || activeTopic?.name || 'this place';
    startNarration(
      place,
      'understandCard',
      {
        topicId: activeTopic?.id || '',
        cardTitle: String(point?.title || '').trim(),
        cardBody: String(point?.body || '').trim(),
      },
      locationData,
      null,
    );
  };

  const handleUnderstandClick = () => {
    ensureMinHeightVh(45);
    setActiveTab('understand-tab');
    if (!activeTopicId || !understandPlaceName) {
      return;
    }

    void fetchUnderstandingConcepts(activeTopicId, {
      languageCode: userPreferredWikiLangCode || viewportWikiLanguage || 'en',
    }).catch((err: any) => {
      const code = String(err?.message || 'REQUEST_FAILED');
      if (code === 'quotaExceeded' || code === 'RESOURCE_EXHAUSTED' || code === '429') {
        setShowQuotaModal(true);
        return;
      }
      if (code === 'unauthenticated' || code === 'AUTH_REQUIRED') {
        loginMachine.requestLogin('auth-required', { preferQuickPrompt: true });
      }
    });
  };

  const handleUnderstandMoreClick = () => {
    if (!activeTopicId || !understandPlaceName || understandStreaming || understandLoading) {
      return;
    }

    void fetchMoreUnderstandingConcepts(activeTopicId, {
      languageCode: userPreferredWikiLangCode || viewportWikiLanguage || 'en',
    }).catch((err: any) => {
      const code = String(err?.message || 'REQUEST_FAILED');
      if (code === 'quotaExceeded' || code === 'RESOURCE_EXHAUSTED' || code === '429' || code === 'llmQuotaExceeded') {
        setShowQuotaModal(true);
        return;
      }
      if (code === 'unauthenticated' || code === 'AUTH_REQUIRED') {
        loginMachine.requestLogin('auth-required', { preferQuickPrompt: true });
      }
    });
  };

  const handleSightClick = (sight: any) => {
    if (!sight) return;
    const articlePayload = {
      title: sight.wikiTitle || sight.articleTitle || sight.title || sight.label,
      pageid: sight.id || sight.pageid || sight.link,
      lat: sight.lat ?? sight.coords?.lat,
      lon: sight.lon ?? sight.coords?.lon,
      thumbnail: sight.thumbnail,
      fullurl: sight.link,
      originalDescription: sight.description,
      articleSizeBytes: sight.articleSizeBytes ?? sight.length ?? null,
      isTopSight: true,
    };
    onArticleSelected(articlePayload);
  };

  const normalizeSight = (sight: any) => ({
    ...sight,
    lat: sight?.lat ?? sight?.coords?.lat,
    lon: sight?.lon ?? sight?.coords?.lon,
  });

  const handleSightShowOnMap = (sight: any) => {
    if (!sight) return;
    onTopSightShowOnMap(normalizeSight(sight));
  };

  const handleSightFocus = (sight: any | null) => {
    onTopSightFocus(sight ? normalizeSight(sight) : null);
  };

  // ---- Imperative handle ----------------------------------------------
  useImperativeHandle(ref, () => ({
    handlePeriodsClick,
    handleFoodClick,
    switchToLocation: (locData: any) => {
      if (!locData) return;
      const id = locationToTopicId(locData);
      const locTopic: LocationTopic = {
        id,
        type: 'location',
        name: getLocationName(locData),
        lat: locData.lat,
        lng: locData.lon,
        wikidataTag: locData.topic_wikidata,
        population: locData.topic_population,
      };
      setTopic(locTopic);
    },
  }));

  // ---- Tab content renderers ------------------------------------------
  const renderFoodTab = () => (
    <LocalFoodV2
      topicId={activeTopic?.id}
      userLanguageLabel={userLanguageLabel}
      viewportWikiLanguage={viewportWikiLanguage}
      isGuestAccount={isGuestAccount}
      onSignInClick={onSignInClick}
    />
  );

  const renderErasTab = () => (
    <div className="eras-section">
      <HistoricalPeriodsV2
        topic={activeTopic}
        topicId={activeTopic?.id}
        userLanguageLabel={userLanguageLabel}
        startNarration={startNarration}
        locationData={locationData}
        isGuestAccount={isGuestAccount}
        onSignInClick={onSignInClick}
      />
    </div>
  );

  const renderUnderstandTab = () => (
    <div className="eras-section understand-section">
      {(understandLoading || understandStreaming) && understandPoints.length === 0 && (
        <ThinkingIndicator style={{ padding: '10px 0' }} />
      )}

      {!understandLoading && !understandStreaming && understandError && understandPoints.length === 0 && (
        <div style={{ color: '#fca5a5' }}>{understandError}</div>
      )}

      {understandPoints.length > 0 && (
        <div ref={understandSliderRef} className="understand-points-list slider-horizontal">
          {understandPoints.map((point, idx) => (
            <div key={`${point.title || 'concept'}-${idx}`} className="understand-point-item">
              <div className="understand-point-title-row">
                <div className="understand-point-title">{point.title}</div>
                <button
                  className="understand-point-speak-btn"
                  onClick={() => handleUnderstandPointRead(point)}
                  aria-label={`Read card: ${point.title}`}
                  title="Read this card"
                >
                  <i className="fas fa-volume-up" aria-hidden="true" />
                </button>
              </div>
              <div className="understand-point-body">{point.body}</div>
            </div>
          ))}
          <div className="understand-point-item understand-more-card">
            <button
              className="understand-more-btn"
              type="button"
              onClick={handleUnderstandMoreClick}
              disabled={understandLoading || understandStreaming}
              aria-label="understand more"
            >
              <i className="fas fa-plus" aria-hidden="true" />
              <span>{understandStreaming ? 'Thinking...' : 'understand more'}</span>
            </button>
          </div>
        </div>
      )}

      {!understandLoading && !understandStreaming && understandError && understandPoints.length > 0 && (
        <div className="understand-inline-error">{understandError}</div>
      )}

      {understandStreaming && understandPoints.length > 0 && (
        <ThinkingIndicator className="understand-stream-status" />
      )}
    </div>
  );

  const renderAudioTab = () => (
    <div className="audio-guide-row">
      {[
        { id: 'overview-tab', icon: 'fas fa-play', label: 'Overview' },
        { id: 'history-tab', icon: 'fas fa-landmark', label: 'History' },
        { id: 'anecdote-tab', icon: 'fas fa-face-laugh', label: 'Anecdote' },
      ]
        .filter((tab) => tab.id !== 'anecdote-tab' || showAnecdoteTab)
        .map((tab) => (
          <button
            key={tab.id}
            className="audio-guide-btn"
            onClick={() => {
              ensureMinHeightVh(AUDIO_GUIDES_MIN_HEIGHT_VH);
              const title = activeTopic?.name || '';
              const currentArticle = isWiki ? article : null;
              if (tab.id === 'overview-tab') {
                startNarration(title, 'overview', { topicId: activeTopic?.id || '' }, locationData, currentArticle);
              } else if (tab.id === 'history-tab') {
                startNarration(title, 'historyOverview', { topicId: activeTopic?.id || '' }, locationData, currentArticle);
              } else if (tab.id === 'anecdote-tab') {
                startNarration(title, 'anecdote', { topicId: activeTopic?.id || '' }, locationData, currentArticle);
              }
            }}
          >
            <i className={`${tab.icon} audio-guide-btn-icon`} />
            <span className="audio-guide-btn-label">{tab.label}</span>
          </button>
        ))}
    </div>
  );

  const renderWikipediaTab = () => (
    <WikipediaTabV2
      topicId={activeTopic?.id}
      viewportWikiLanguage={viewportWikiLanguage}
      userPreferredWikiLangCode={userPreferredWikiLangCode}
    />
  );

  // ---- Title text -----------------------------------------------------
  const titleText = isPrompt
    ? 'How to use Curio:'
    : isLocation
      ? (activeTopic?.name || 'Current Location')
      : (activeTopic?.name || '');

  // =====================================================================
  // RENDER
  // =====================================================================
  return (
    <div
      ref={cardRef}
      className="details-card"
      onPointerDownCapture={onCardPointerDown}
      style={{ height: heightPx, transition: isDragging ? 'none' : 'height 0.25s ease' }}
    >
      <div className="drag-bar" onPointerDown={onCardPointerDown} />

      {/* ---- Header --------------------------------------------------- */}
      <div className="details-card-header" onPointerDown={onCardPointerDown}>
        {!isPrompt && audioStatus !== 'idle' && (
          <div
            ref={audioWidgetAnchorRef}
            className="details-card-audio-widget-wrap details-card-audio-widget-wrap--header"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <AudioStatusWidget
              embedded
              status={audioStatus}
              title={audioTitle}
              mode={audioMode}
              onPauseToggle={onAudioPauseToggle}
              onTogglePlayPause={onAudioTogglePlayPause}
              onStop={onAudioStop}
              onSeek={onAudioSeek}
              currentTime={audioCurrentTime}
              duration={audioDuration}
            />
          </div>
        )}

        <div className="details-card-header-main-row">
          <h3 className="details-title" style={{ marginRight: '10px' }}>
            {titleText}
          </h3>

          {/* Population badge (directly after location/title text) */}
          {isLocation && locationTopic?.wikidataTag && (
            <span className="header-population-badge">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 21a8 8 0 0 0-16 0" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span>{formatPopulationCompact(populationValue) || '\u2014'}</span>
            </span>
          )}

          {/* Location button – pinned to the far right */}
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0, minWidth: 0 }}>
            {shouldShowLocationButton && (
              <button
                className="floating-location-btn"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => {
                  console.log('[DetailsCardV2] Location button pressed – activating suggested topic.');
                  const lat = Number(locationData?.lat);
                  const lon = Number(locationData?.lon ?? locationData?.lng);
                  if (Number.isFinite(lat) && Number.isFinite(lon)) {
                    setLastLocationButtonPressPosition({ lat, lon });
                  }
                  if (!suggestedTopic) {
                    handleLocationButtonClick();
                    return;
                  }
                  activateSuggestedTopic({
                    userLanguageLabel,
                    viewportWikiLanguage,
                    userPreferredWikiLangCode,
                  });
                }}
                aria-label={`Location: ${locationButtonTopicName || 'Current Location'}`}
              >
                <i className="fas fa-chevron-left" style={{ fontSize: '0.65rem', opacity: 0.7 }} aria-hidden="true" />
                <span className="floating-location-label">{locationButtonTopicName || 'Location'}</span>
                <div style={{ position: 'relative', display: 'inline-flex' }}>
                  <span className="material-icons" style={{ fontSize: '18px' }} aria-hidden="true">map</span>
                  {hasMovedSignificantly() && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '-2px',
                        right: '-2px',
                        width: '8px',
                        height: '8px',
                        backgroundColor: 'limegreen',
                        borderRadius: '50%',
                        zIndex: 1,
                      }}
                    />
                  )}
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ---- Card body ------------------------------------------------ */}
      <div className="details-card-content">
        <div className={`content-column ${showCardLoginPrompt ? 'content-column--login-blurred' : ''}`} ref={contentRef}>

          {/* Onboarding prompt (no GPS) */}
          {isPrompt && (
            <main className="info-prompt-main">
              <div className="info-prompt-grid">
                <div className="info-prompt-number">1.</div>
                <div className="info-prompt-content">
                  <h2 className="info-prompt-title">Zoom to your area of interest!</h2>
                </div>
                <div className="info-prompt-number" style={{ alignSelf: 'center' }}>2.</div>
                <div className="info-prompt-content">
                  <div className="info-prompt-cards-with-or">
                    <div className="info-prompt-card">
                      <span className="material-icons info-prompt-icon info-prompt-icon-orange">map</span>
                      <p className="info-prompt-card-text">Click map button to learn about a region.</p>
                    </div>
                    <div className="info-prompt-or">or</div>
                    <div className="info-prompt-card">
                      <span className="material-icons info-prompt-icon info-prompt-icon-rose">place</span>
                      <p className="info-prompt-card-text">Click on markers to learn about POIs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          )}

          {/* ---- Primary media slot (now above tabs) ------------------- */}
          {!isPrompt && (
            <div className="primary-media-slot">
              {isWiki && (
                <WikiImagesSliderV2
                  topicId={activeTopic?.id}
                  viewportWikiLanguage={viewportWikiLanguage}
                  onPointerDown={onCardPointerDown}
                />
              )}

              {isLocation && (
                <TopSightsCarouselV2
                  onSightSelect={handleSightClick}
                  onSightShowOnMap={handleSightShowOnMap}
                  onSightFocus={handleSightFocus}
                  preferredLanguageCode={userPreferredWikiLangCode || viewportWikiLanguage}
                  onPointerDown={onCardPointerDown}
                />
              )}
            </div>
          )}

          {/* ---- Article description (wiki mode) ------------------------ */}
          {isWiki && !isPrompt && (wikiTopic?.conciseOverview || wikiTopic?.description) && (
            <div
              className="article-description-container"
              style={{ padding: '8px 2px 8px 2px', fontSize: '0.75em', lineHeight: '1.4' }}
            >
              <p className="article-description" style={{ margin: 0 }}>
                {wikiTopic?.conciseOverview || trimOverviewText(String(wikiTopic?.description || ''), 140)}
              </p>
              {!!wikiTopic?.conciseOverviewLoading && (
                <div style={{ marginTop: '4px', opacity: 0.7, fontSize: '0.72em' }}>Refreshing concise overview…</div>
              )}
            </div>
          )}

          {/* ---- Sticky Nav Bar (single-row tabs) --------------------- */}
          <nav className="details-sticky-nav">
            {!isPrompt && (
              <div ref={mainTabsSliderRef} className="details-nav-row details-nav-row--tabs slider-horizontal">
                {([
                  { id: 'audio-tab', icon: ['fas fa-headphones'], label: 'Audio Guides', colorClass: 'nav-clr-wiki', hideForArticle: false },
                  { id: 'understand-tab', icon: ['fas fa-lightbulb'], label: understandButtonLabel, colorClass: 'nav-clr-understand', hideForArticle: false },
                  { id: 'eras-tab', icon: ['fas fa-list-alt'], label: 'Historical Eras', colorClass: 'nav-clr-eras', hideForArticle: false },
                  { id: 'food-tab', icon: ['fas fa-utensils'], label: 'Local Food', colorClass: 'nav-clr-food', hideForArticle: true },
                  { id: 'wikipedia-tab', icon: ['fas fa-globe'], label: 'Wiki', colorClass: 'nav-clr-wiki', hideForArticle: false },
                ] as const)
                  .filter((tab) => {
                    if (tab.hideForArticle && isWiki) return false;
                    if (tab.id === 'wikipedia-tab' && !isWiki && !isLocation) return false;
                    return true;
                  })
                  .map((tab) => {
                    const isWikiDisabled = tab.id === 'wikipedia-tab' && isLocation && !hasLocationWikiAvailable;
                    return (
                      <button
                        key={tab.id}
                        className={`nav-tab nav-tab--main ${tab.colorClass} ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'understand-tab' ? 'nav-tab-understand' : ''}`}
                        disabled={isWikiDisabled}
                        aria-disabled={isWikiDisabled}
                        title={isWikiDisabled ? 'Wikipedia link not available for this location yet' : undefined}
                        style={isWikiDisabled ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                        onClick={() => {
                          if (isWikiDisabled) return;
                          setActiveTab(tab.id);
                          if (tab.id === 'audio-tab') {
                            ensureMinHeightVh(AUDIO_GUIDES_MIN_HEIGHT_VH);
                          }
                          switch (tab.id) {
                            case 'understand-tab': handleUnderstandClick(); break;
                            case 'eras-tab': handlePeriodsClick(); break;
                            case 'food-tab': handleFoodClick(); break;
                            case 'wikipedia-tab': handleWikipediaTabClick(); break;
                            default: break;
                          }
                        }}
                      >
                        <div className="nav-tab-icons">
                          {tab.icon.map((cls, idx) => (
                            <i key={idx} className={`${cls} nav-icon`} />
                          ))}
                        </div>
                        <span className="nav-tab-label">{tab.label}</span>
                      </button>
                    );
                  })}
              </div>
            )}
          </nav>

          {/* ---- Tab content area ------------------------------------- */}
          {!isPrompt && (
            <div className="tab-content-area">
              {activeTab === 'audio-tab' && renderAudioTab()}
              {activeTab === 'understand-tab' && renderUnderstandTab()}
              {activeTab === 'eras-tab' && renderErasTab()}
              {activeTab === 'food-tab' && renderFoodTab()}
              {activeTab === 'wikipedia-tab' && renderWikipediaTab()}
            </div>
          )}
        </div>
        {showCardLoginPrompt && (
          <SigninPopup
            onClose={() => loginMachine.closePrompt()}
            isAnonymous={isLoginAnonymous}
            error={loginUi.kind === 'DetailsCardLoginPrompt' ? loginUi.error : undefined}
            cardOverlay
          />
        )}
      </div>

      {/* ---- Floating history button above the card (left side) ------- */}
      {(showPwaInstallButton || onOpenUserHistory) && (
        <div
          style={{
            position: 'absolute',
            left: '16px',
            bottom: '100%',
            marginBottom: '12px',
            zIndex: 9999,
            pointerEvents: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            alignItems: 'flex-start',
          }}
        >
          {showPwaInstallButton && (
            <button
              onClick={onPwaInstallClick}
              style={{
                background: 'rgba(50, 50, 50, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                lineHeight: '1',
                color: '#f0f0f0',
                width: '38px',
                height: '38px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}
              aria-label="Install app"
              title="Install app"
            >
              <i className="fas fa-download" style={{ fontSize: '14px' }} />
            </button>
          )}
          {onOpenUserHistory && (
            <button
              ref={historyButtonRef}
              onClick={onOpenUserHistory}
              style={{
                background: 'rgba(50, 50, 50, 0.85)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                lineHeight: '1',
                color: '#f0f0f0',
                width: '38px',
                height: '38px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}
              aria-label="User history"
            >
              <i className="fas fa-history" style={{ fontSize: '14px' }} />
            </button>
          )}
        </div>
      )}

      {/* ---- Floating buttons above the card -------------------------- */}
      <div
        style={{
          position: 'absolute',
          right: '16px',
          bottom: '100%',
          marginBottom: '12px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '10px',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        {/* Get Pro button */}
        <button
          className="get-pro-button"
          onClick={() => {
            if (isGuestAccount) {
              loginMachine.requestLogin('manual', { preferQuickPrompt: false });
            } else {
              setShowQuotaModal(true);
            }
          }}
          style={{
            width: '38px',
            height: '38px',
            borderRadius: '50%',
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.3s ease, transform 0.3s ease',
            pointerEvents: 'auto',
          }}
          aria-label="Get Pro"
        >
          <span className="material-icons" style={{ fontSize: '22px' }}>add</span>
        </button>

        {/* Map kind button – below Get Pro */}
        {onToggleMapType && (
          <button
            onClick={onToggleMapType}
            style={{
              background: 'rgba(50, 50, 50, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: 0,
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              lineHeight: '1',
              color: '#f0f0f0',
              width: '38px',
              height: '38px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'auto',
            }}
            aria-label="Toggle map type"
          >
            <i className="fas fa-layer-group" style={{ fontSize: '14px' }} />
          </button>
        )}

        {/* Go to My Location button */}
        {onGoToMyLocation && (
          <button
            onClick={onGoToMyLocation}
            style={{
              background: 'rgba(50, 50, 50, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              padding: 0,
              borderRadius: '50%',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              lineHeight: '1',
              color: '#f0f0f0',
              width: '38px',
              height: '38px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'auto',
            }}
            aria-label="Go to my location"
            dangerouslySetInnerHTML={{
              __html: isMapCenteredOnUser
                ? `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                    <g>
                      <circle cx="12" cy="12" r="5" fill="#4285F4" stroke="white" stroke-width="2"/>
                      <circle cx="12" cy="12" r="9" stroke="#4285F4" stroke-width="2" opacity="0.3" fill="none"/>
                    </g>
                  </svg>`
                : `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24">
                    <g fill="none" stroke="white" stroke-width="2">
                      <circle cx="12" cy="12" r="5" stroke="white"/>
                      <circle cx="12" cy="12" r="9" opacity="0.4"/>
                    </g>
                  </svg>`,
            }}
          />
        )}


      </div>
      {/* ---- Quota Modal ---------------------------------------------- */}
      {showQuotaModal && (
        <QuotaModal onClose={() => setShowQuotaModal(false)} />
      )}

      {/* ---- Image Popup Modal (TODO) --------------------------------- */}
      {/* TODO: image popup overlay with navigation */}
    </div>
  );
});

DetailsCardV2.displayName = 'DetailsCardV2';

export default DetailsCardV2;
